export const paymentSources = {
    instaPaidAccount: 'InstaPaidAccount',
    instaPaidEurAccount: 'InstaPaidEurAccount',
    companyAccount: 'CompanyAccount'
};

// Payment combinations that route payouts through a named InstaPaid house
// account and therefore require an explicit house-account selection.
export const HOUSE_ACCOUNT_PAYMENT_COMBINATIONS = [
    'InstaPaid DKK',
    'InstaPaid EUR'
];

export const paymentProviders = {
    aiia: 'Aiia',
    danskeBank: 'DanskeBank'
};

export const approvalTypes = {
    manual: 'Manual',
    automatic: 'Automatic'
};

// Named individually because the upload UI has to tell these two apart to pick
// the right importer — membership alone isn't enough, and the alternative is
// bare 'DataLon' string literals at the call sites.
export const DATEV_LODAS_FILE = 'DatevLodasFile';
export const DATA_LON = 'DataLon';

/**
 * Lessor is the one salary system whose tax cards arrive as a Lessor CSV; every
 * other system uploads a fixed-width SKAT file. The two go through different
 * parsers on the same endpoint, so the upload control has to offer the right
 * extension — a file the backend does not recognise is skipped, not rejected.
 */
export const LESSOR = 'Lessor';

/**
 * Salary systems with no API integration: bank details reach InstaPaid only
 * through an export the admin uploads by hand.
 *
 * Deliberately not the full provider list — Zenegy, Intect, ProLøn and the rest
 * sync over their own APIs, so adding one here would put an upload control in
 * front of a company that has no file to upload.
 */
export const FILE_UPLOAD_PROVIDER_NAMES = [DATEV_LODAS_FILE, DATA_LON];

/** Gate for every manual salary-file upload control. */
export const requiresManualSalaryFileUpload = (salaryProvider?: string) =>
    !!salaryProvider && FILE_UPLOAD_PROVIDER_NAMES.includes(salaryProvider);

/**
 * Gate for the tax-card upload control.
 *
 * The SKAT / Lessor tax-card file is provider-agnostic, so this is an exclusion
 * rather than a list: DATEV LODAS is German payroll and has no SKAT tax cards
 * at all, and DataLon carries tax-card data inside its own employee export.
 * Both would get a dead control, and a new provider should inherit the upload
 * without anyone remembering to add it here.
 */
export const supportsTaxCardUpload = (salaryProvider?: string) =>
    !requiresManualSalaryFileUpload(salaryProvider);

export const ENABLE_SHIFT_TYPES_FOR_PROVIDERS = [
    'Planday',
    'TitanPlan',
    'Staff',
    'Quinyx',
    'SameSystem',
    'WorkForce',
    'Workfeed'
];

export const AUTO_APPROVAL_TIME_PROVIDERS = [
    'Planday',
    'Staff',
    'Tamigo',
    'SameSystem',
    'TitanPlan',
    'Quinyx',
    'WorkForce',
    'Workfeed'
];
export const ALLOW_CONNECTION_TIME_REGISTRATION_EDIT = [
    'Planday',
    'Zenegy',
    'Tamigo',
    'SameSystem',
    'TitanPlan',
    'Staff',
    'Quinyx',
    'WorkForce',
    'Workfeed'
];

export const CREDENTIAL_BASED_AUTH_PROVIDERS = [
    'Planday',
    'Tamigo',
    'SameSystem',
    'TitanPlan',
    'Staff',
    'Quinyx',
    'WorkForce',
    'Workfeed'
];

export const OAUTH_BASED_AUTH_PROVIDERS = ['Zenegy'];

// Providers whose shifts are served from a cache that a background job refreshes
// on a schedule, so Backoffice needs a way to pull the latest shifts on demand.
export const MANUAL_SHIFTS_CACHE_REFRESH_PROVIDERS = ['Planday'];

export const PER_DEPARTMENT_TIPS_SALARY_TYPE_PROVIDERS = ['Zenegy', 'Intect'];

export const CREDENTIAL_BASED_PAYROLL_PROVIDERS = [
    'Intect',
    'ProLoen',
    'Epos',
    'Lessor',
    'DagrofaLon',
    'DataLon',
    'D365FinanceOperations',
    'DatevLodasFile'
];

// Supported document languages configuration
export const SUPPORTED_DOCUMENT_LANGUAGES = [
    {
        code: 'da',
        name: 'Danish',
        flag: '/src/icons/flags/denmark.svg'
    },
    {
        code: 'en',
        name: 'English',
        flag: '/src/icons/flags/uk.svg'
    }
    // Add more languages here as needed
    // {
    //     code: 'sv',
    //     name: 'Swedish',
    //     flag: '/src/icons/flags/sweden.svg'
    // }
] as const;
